import React from 'react';
import * as S from './styles';
import { useTranslationHelper } from 'src/contexts/dependencyManager/utils';
import { Address } from 'src/types/address';
import { IconPin } from '@veneer/core';
import LoadingHandler from '../LoadingHandler/LoadingHandler';
import { CardError } from '../CardError/CardError';
import useShellContext from 'src/hooks/useShellContext';

export type ShippingAddressProps = {
  shippingAddress?: Address | null;
  isLoading?: boolean;
  error?: Error | null;
  showChangeButton?: boolean;
};

export const ShippingAddress: React.FC<ShippingAddressProps> = ({
  shippingAddress,
  isLoading,
  error,
  showChangeButton = true
}) => {
  const getText = useTranslationHelper();
  const { navigation } = useShellContext();

  const handleNavigateToShippingBilling = (
    e: React.MouseEvent | React.KeyboardEvent
  ) => {
    e.preventDefault();
    navigation?.push('/account-details/shipping-billing');
  };
  return (
    <S.ShippingAddressCard>
      <LoadingHandler
        loading={isLoading}
        error={Boolean(error)}
        $position="absolute"
        loadingText={getText('loading', 'Loading...')}
        customError={
          <CardError
            forceHeight={false}
            icon={<IconPin size={32} />}
            texts={[
              <>
                <p>
                  {getText(
                    'shippingAddress.error.title.paragraph1',
                    'Something went wrong.'
                  )}
                </p>
                <p>
                  {getText(
                    'shippingAddress.error.title.paragraph2',
                    'Your shipping address information did not load.'
                  )}
                </p>
              </>,
              getText(
                'shippingAddress.error.description',
                'Please refresh the page.'
              )
            ]}
          />
        }
      >
        <S.CardContent>
          <S.CardHeader>
            <IconPin size={32} />
            <S.CardTitle>
              {getText('shippingAddress.title', 'Shipping Address')}
            </S.CardTitle>
          </S.CardHeader>

          <S.AddressDetailsContainer>
            <S.UserAddressDetails>
              <S.UserName>
                {shippingAddress &&
                  `${shippingAddress.firstName} ${shippingAddress.lastName}`}
              </S.UserName>
              <S.AddressLine>
                {shippingAddress &&
                  (shippingAddress.address2
                    ? `${shippingAddress.address}, ${shippingAddress.address2}`
                    : shippingAddress.address)}
              </S.AddressLine>
              <S.AddressLine>
                {shippingAddress &&
                  `${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.postalCode}`}
              </S.AddressLine>
              <S.AddressLine>
                {shippingAddress && shippingAddress.phoneNumber}
              </S.AddressLine>
            </S.UserAddressDetails>
          </S.AddressDetailsContainer>

          {showChangeButton && !isLoading && !error && (
            <S.ManageShippingAddressLink
              href="#"
              onClick={handleNavigateToShippingBilling}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleNavigateToShippingBilling(event);
                }
              }}
              id="shipping-billing-link"
            >
              {getText(
                'shippingAddress.manageShippingAddress',
                'Manage shipping address'
              )}
            </S.ManageShippingAddressLink>
          )}
        </S.CardContent>
      </LoadingHandler>
    </S.ShippingAddressCard>
  );
};
